import { watchSession, clearSession as clearPortalSession } from '../shared/auth/portalSession';
import { getDashboard } from './api/dashboardApi';
import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import SessionWarning from './components/SessionWarning';
import AppointmentsSummary from './components/AppointmentsSummary';
import Services from './components/Services';
import Schedule from './components/Schedule';
import Holidays from './components/Holidays';
import InternalBooking from './components/InternalBooking';
import Login from './components/Login';
import Technicians from './components/Technicians';
import { DraftAppointments } from './components/DraftAppointments';
import { AppointmentData, getAppointmentsApi, type AdminScheduleSlot } from './api/appointmentApi';
import { Appointment, Service, View, DaySchedule, TimeSlot, WEEK_DAYS, AttendanceStatus, SupportProvider } from './types';
import { AdminUser } from './api/adminApi';
import { getServicesApi } from './api/serviceApi';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const Reports = React.lazy(() => import('./components/Reports'));
const ReminderHistory = React.lazy(() => import('./components/ReminderHistory'));

const PageLoader: React.FC = () => (
    <div className="flex-1 flex items-center justify-center p-10">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600" aria-label="טוען" />
    </div>
);

const generateTimeSlots = (start: string, end: string, interval: number): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    let currentTime = new Date(`1970-01-01T${start}:00`);
    const endTime = new Date(`1970-01-01T${end}:00`);

    while (currentTime < endTime) {
        slots.push({
            time: currentTime.toTimeString().substring(0, 5),
            isBooked: Math.random() < 0.2 // Randomly book some slots for demo
        });
        currentTime.setMinutes(currentTime.getMinutes() + interval);
    }
    return slots;
};

const initialSchedule: DaySchedule[] = WEEK_DAYS.map(day => ({
    day: day.label,
    morning: generateTimeSlots('09:00', '11:30', 5),
    afternoon: generateTimeSlots('13:30', '18:40', 5),
    isOpen: day.id !== 'Sunday' // Sunday is closed by default
}));

const appointmentToView = (apt: AppointmentData): Appointment => ({
    id: apt.appointmentId || '', patientName: apt.fullName, time: apt.selectedTime,
    date: apt.selectedDay, service: apt.serviceName || (apt.serviceType === 1 ? 'צילום רנטגן' : 'אולטרסאונד'),
    village: apt.village, phoneNumber: apt.phoneNumber, idCard: apt.idCard || '',
    attendanceStatus: 'Pending' as AttendanceStatus, supportProvider: (apt.provider || 'פרטי') as SupportProvider,
    isSynced: apt.isSynced || false, clinicName: apt.clinicName, createdBy: apt.createdBy,
    createdAt: apt.createdAt, isReserveSlot: apt.isReserveSlot || false, status: apt.status,
    confirmationStatus: apt.confirmationStatus, reminderSent: apt.reminderSent || false,
});

const AppContent: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [services, setServices] = useState<Service[]>([]);
    const [draftToEdit, setDraftToEdit] = useState<AppointmentData | undefined>(undefined);
    const [draftCount, setDraftCount] = useState<number>(0);
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loadingAppointments, setLoadingAppointments] = useState(false);
    const [appointmentsError, setAppointmentsError] = useState<string | null>(null);
    const appointmentRequest = React.useRef(0);
    const [selectedDate, setSelectedDate] = useState<string | null>(() => {
        // A fresh visit opens today's schedule, not a previously saved date.
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Asia/Jerusalem',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date());
    });
    const [schedule, setSchedule] = useState<DaySchedule[]>(initialSchedule);
    const [user, setUser] = useState<AdminUser | null>(null);
    const [tokenExpiry, setTokenExpiry] = useState<number>(0);
    const [loading, setLoading] = useState(true);

  React.useEffect(() => watchSession<AdminUser>('admin', session => {
    setUser(session?.user ?? null);
    setTokenExpiry(session ? session.expiresAt * 1000 : 0);
    setLoading(false);
    if (!session) { setAppointments([]); setDraftCount(0); setSelectedDate(null); }
  }), []);

  const clearSession = () => clearPortalSession('admin');
  React.useEffect(() => {
    if (!user?.email) { setServices([]); return; }
    if (location.pathname !== '/appointments') return;
    let current = true;
    getServicesApi().then(data => { if (current) setServices(data); })
      .catch(error => console.error('Failed to load service filters:', error));
    return () => { current = false; };
  }, [user?.email, location.pathname]);
  const handleLoginSuccess = (_user: AdminUser) => { /* saveSession notifies the watcher. */ };

    const handleLogout = () => {
        setUser(null);
        setTokenExpiry(0);
        clearSession();
        // Reset application state
        setAppointments([]);
        setDraftCount(0);
        setSelectedDate(null);
    };

    const handleRefreshSession = () => {
        // Clear current session and show login
        setUser(null);
        setTokenExpiry(0);
        clearSession();
    };

    // Load appointments from API
    const loadAppointments = React.useCallback(async (dayFilter?: string | null, creationRange?: { start: string; end: string }) => {
        if (!user) return; // Don't load if not authenticated
        const request = ++appointmentRequest.current;
        
        try {
            setLoadingAppointments(true);
            setAppointmentsError(null);
            setAppointments([]);
            const params: any = { status: 'completed,closed' };
            
            if (dayFilter) {
                params.day = dayFilter;
            } else if (creationRange) {
                params.startDate = creationRange.start;
                params.endDate = creationRange.end;
            }
            
            const data = await getAppointmentsApi(params);
            if (request !== appointmentRequest.current) return;
            
            const transformedAppointments = data.map(appointmentToView);
            
            setAppointments(transformedAppointments);
        } catch (error) {
            if (request !== appointmentRequest.current) return;
            setAppointmentsError('לא ניתן לטעון תורים. נסה לבחור את התקופה שוב.');
            console.error('Failed to load appointments:', error);
        } finally {
            if (request === appointmentRequest.current) setLoadingAppointments(false);
        }
    }, [user]);

    const loadAppointmentsForCurrentView = React.useCallback(() => {
        if (location.pathname === '/appointments' && selectedDate === null) return loadAppointments(null);
    }, [loadAppointments, location.pathname, selectedDate]);

    React.useEffect(() => {
        if (user && location.pathname === '/appointments') {
            if (selectedDate === null) loadAppointments(null);
            else {
                // The day table owns its request; invalidate any older list/report read.
                appointmentRequest.current += 1;
                setLoadingAppointments(false);
                setAppointmentsError(null);
            }
        }
    }, [loadAppointments, location.pathname, selectedDate, user]);

    const loadReportRange = React.useCallback((range: { start: string; end: string }) => loadAppointments(null, range), [loadAppointments]);

    const handleScheduleLoaded = React.useCallback((rows: AdminScheduleSlot[], day: string, scheduleType: 'US' | 'Xray') => {
        appointmentRequest.current += 1;
        setAppointments(rows.flatMap(slot => slot.appointments.map(apt => appointmentToView({
            ...apt, fullName: apt.patientName, idCard: apt.idCard || '',
            selectedDay: day, selectedDayLabel: day, selectedTime: slot.time,
            serviceType: scheduleType === 'Xray' ? 1 : 2, status: apt.status as AppointmentData['status'],
        }))));
    }, []);

    // Load draft count on mount - only if user is authenticated
    React.useEffect(() => {
        if (!user) return;
        
        const loadDraftCount = async () => {
            try {
                const drafts = await getDashboard<{ count: number }>('draft-count');
                setDraftCount(drafts.count);
            } catch (err) {
                console.error('Failed to load draft count:', err);
            }
        };
        loadDraftCount();
    }, [user]);

    const refreshDraftCount = async () => {
        if (!user) return; // Don't make API calls if not authenticated
        
        try {
            const drafts = await getDashboard<{ count: number }>('draft-count');
            setDraftCount(drafts.count);
        } catch (err) {
            console.error('Failed to refresh draft count:', err);
        }
    };

    const updateSchedule = (dayName: string, isOpen: boolean) => {
        setSchedule(prev => prev.map(day => day.day === dayName ? { ...day, isOpen } : day));
    };
    
    const updateAppointmentStatus = (id: string, status: AttendanceStatus) => {
        setAppointments(prev => prev.map(app => app.id === id ? { ...app, attendanceStatus: status } : app));
    };

    const getCurrentView = (): View => {
        const path = location.pathname;
        if (path === '/appointments') return 'appointmentsSummary';
        if (path === '/services') return 'services';
        if (path === '/technicians') return 'technicians';
        if (path === '/schedule') return 'schedule';
        if (path === '/holidays') return 'holidays';
        if (path === '/booking') return 'booking';
        if (path === '/drafts') return 'drafts';
        if (path === '/reports') return 'reports';
        if (path === '/reminders') return 'reminderHistory';
        return 'dashboard';
    };

    const setCurrentView = (view: View) => {
        const paths = {
            dashboard: '/',
            appointmentsSummary: '/appointments',
            services: '/services',
            technicians: '/technicians',
            schedule: '/schedule',
            holidays: '/holidays',
            booking: '/booking',
            drafts: '/drafts',
            reports: '/reports',
            reminderHistory: '/reminders'
        };
        navigate(paths[view]);
    };

    const handleContinueBooking = (draft: AppointmentData) => {
        setDraftToEdit(draft);
        navigate('/booking');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
        );
    }

    if (!user) {
        return <Login onLoginSuccess={handleLoginSuccess} />;
    }

    return (
        <>
            <SessionWarning expiryTime={tokenExpiry} onRefresh={handleRefreshSession} />
            <div className="flex h-screen bg-slate-100 overflow-hidden">
                <Sidebar currentView={getCurrentView()} setCurrentView={setCurrentView} draftCount={draftCount} />
                <div className="flex-1 flex flex-col mr-64 min-w-0 overflow-hidden">
                <Header 
                    currentView={getCurrentView()} 
                    searchTerm={searchTerm} 
                    onSearchChange={setSearchTerm}
                    user={user}
                    onLogout={handleLogout}
                />
                <main className="flex-1 overflow-y-auto overflow-x-hidden w-full">
                    {appointmentsError && ((location.pathname === '/appointments' && selectedDate === null) || location.pathname === '/reports') && <p role="alert" className="p-4 text-red-700">{appointmentsError}</p>}
                    <React.Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={
                            <Dashboard onDateChange={setSelectedDate} />
                        } />
                        <Route path="/appointments" element={
                            <AppointmentsSummary 
                                appointments={appointments} 
                                searchTerm={searchTerm} 
                                onUpdateStatus={updateAppointmentStatus} 
                                services={services}
                                initialSelectedDate={selectedDate}
                                onDateChange={setSelectedDate}
                                loading={loadingAppointments}
                                onAppointmentCreated={loadAppointmentsForCurrentView}
                                onScheduleLoaded={handleScheduleLoaded}
                                userName={user?.name}
                            />
                        } />
                        <Route path="/reminders" element={<ReminderHistory />} />
                        <Route path="/services" element={<Services />} />
                        <Route path="/technicians" element={<Technicians />} />
                        <Route path="/schedule" element={<Schedule />} />
                        <Route path="/holidays" element={<Holidays />} />
                        <Route path="/reports" element={<Reports appointments={appointments} services={services} onRangeChange={loadReportRange} loading={loadingAppointments} />} />
                        <Route path="/booking" element={
                            <InternalBooking 
                                initialDraft={draftToEdit} 
                                onDraftLoaded={() => setDraftToEdit(undefined)}
                                onDraftCompleted={refreshDraftCount}
                                onAppointmentCreated={loadAppointmentsForCurrentView}
                                userName={user?.name}
                            />
                        } />
                        <Route path="/drafts" element={
                            <DraftAppointments 
                                onContinueBooking={handleContinueBooking}
                                onDraftCountChange={setDraftCount}
                            />
                        } />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                    </React.Suspense>
                </main>
            </div>
        </div>
        </>
    );
};

const App: React.FC = () => {
    if (!GOOGLE_CLIENT_ID) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
                    <h1 className="text-2xl font-bold text-red-600 mb-4">⚠️ Configuration Error</h1>
                    <p className="text-slate-700 mb-4">
                        Google Client ID is not configured. Please add it to your <code className="bg-slate-100 px-2 py-1 rounded">.env</code> file:
                    </p>
                    <pre className="bg-slate-100 p-4 rounded text-left text-sm overflow-x-auto">
                        VITE_GOOGLE_CLIENT_ID=your-google-client-id
                    </pre>
                    <p className="text-slate-600 text-sm mt-4">
                        See <code>.env.example</code> for reference
                    </p>
                </div>
            </div>
        );
    }

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <BrowserRouter>
                <AppContent />
            </BrowserRouter>
        </GoogleOAuthProvider>
    );
};

export default App;
